import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, ArrowUp } from "lucide-react";
import { API } from "../lib/api";

const SUGGEST = ["Which plan suits me?", "How do I submit a paper?", "Cite the Productivity Paradox study in APA"];

function sessionId() {
  let s = localStorage.getItem("bpj_chat_session");
  if (!s) { s = `web_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("bpj_chat_session", s); }
  return s;
}

async function streamChat(message, onText) {
  const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId(), message }) });
  if (!res.ok || !res.body) throw new Error("unavailable");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (p.startsWith("event: done")) return;
      const line = p.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      try { onText(JSON.parse(line.slice(6)).t || ""); } catch { /* skip */ }
    }
  }
}

function renderMd(t) {
  return t.split(/(\*\*[^*]+\*\*)/g).map((s, i) => (s.startsWith("**") && s.endsWith("**") ? <strong key={i}>{s.slice(2, -2)}</strong> : s.replace(/\*\*/g, "")));
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "Hello — I'm the BPJ Assistant. Ask me for reading picks, citations, submission guidance or the right subscription." }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs, open]);

  const send = async (q) => {
    const m = (q ?? text).trim();
    if (!m || busy) return;
    setText("");
    setBusy(true);
    setMsgs((p) => [...p, { role: "user", content: m }, { role: "assistant", content: "" }]);
    const append = (t) => setMsgs((p) => { const c = [...p]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t }; return c; });
    try { await streamChat(m, append); }
    catch { append("Sorry — I couldn't reach the assistant. Please try again shortly."); }
    setBusy(false);
  };

  return (
    <>
      <motion.button onClick={() => setOpen((v) => !v)} whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }} data-testid="chat-toggle-btn" aria-label="Open assistant"
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-[var(--ink)] text-white grid place-items-center shadow-xl">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && <span className="absolute top-1 right-1 h-3 w-3 rounded-full bg-[var(--red)] ring-2 ring-[var(--paper)]" />}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16 }} transition={{ duration: 0.3 }}
            className="fixed z-50 bottom-24 right-3 left-3 sm:left-auto sm:right-5 sm:w-[400px] h-[min(560px,70vh)] bg-[var(--paper)] border border-[var(--ink)] shadow-2xl flex flex-col" data-testid="chat-panel">
            <div className="px-5 py-4 border-b rule flex items-center justify-between">
              <div><p className="f-serif text-xl">BPJ Assistant</p><p className="eyebrow !text-[0.6rem]">AI · answers may be imperfect</p></div>
              <span className="h-2 w-2 rounded-full bg-emerald-600" />
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-lenis-prevent data-testid="chat-messages">
              {msgs.map((m, i) => (
                <div key={i} className={`text-[0.93rem] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "ml-10 bg-[var(--ink)] text-white px-4 py-2.5" : "mr-6 text-[var(--ink-2)]"}`} data-testid={`chat-msg-${m.role}`}>
                  {m.content ? renderMd(m.content) : <span className="inline-flex gap-1">{[0, 1, 2].map((d) => <motion.span key={d} className="w-1.5 h-1.5 bg-[var(--muted)] rounded-full" animate={{ opacity: [0.2, 1, 0.2] }} transition={{ repeat: Infinity, duration: 1, delay: d * 0.2 }} />)}</span>}
                </div>
              ))}
              {msgs.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGEST.map((s) => <button key={s} onClick={() => send(s)} className="text-xs border rule px-3 py-1.5 hover:border-[var(--ink)] transition-colors" data-testid="chat-suggestion">{s}</button>)}
                </div>
              )}
              <div ref={end} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t rule flex items-center gap-2 px-3 py-3">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask anything about BPJ…" className="flex-1 bg-transparent outline-none px-2 text-[0.95rem]" data-testid="chat-input" />
              <button disabled={busy || !text.trim()} className="h-9 w-9 grid place-items-center bg-[var(--red)] text-white disabled:opacity-30" data-testid="chat-send-btn" aria-label="Send"><ArrowUp size={17} /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
